import styled, { css } from "styled-components";
import { BaseComponentProps } from "../types";
import { alpha } from "../../typography";
import { IconName, SmallPlainIcon } from "../Icon";
import React, { useEffect, useRef, useState } from "react";
import { LabelText, Asterisk, Caption } from "../common";

type StyledTextAreaContainerProps = {
  disabled?: boolean;
  readOnly?: boolean;
  hasError?: boolean;
};

const StyledTextAreaContainer = styled.div<StyledTextAreaContainerProps>`
  position: relative;
  display: flex;
  box-sizing: border-box;
  min-height: 32px;
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-small);
  :focus-within {
    outline: none;
  }
  ${(props) =>
    props.hasError
      ? css`
          border: 1px solid var(--color-error-500);
        `
      : props.disabled || props.readOnly
      ? css`
          border: 1px solid var(--color-primary-200);
          input::placeholder {
            color: var(--color-primary-200);
          }
        `
      : css`
          &:hover {
            border: 1px solid var(--color-primary-800);
          }
          &:focus-within {
            border: 1px solid var(--color-secondary-800);
          }
        `}
`;

const StyledTextArea = styled.textarea`
  display: block;
  min-width: 0px;
  min-height: 100px;
  flex-grow: 1;
  padding: 6px 12px;
  padding-right: 34px;
  background: var(--color-neutral-000-full);
  border: none;
  border-radius: var(--size-border-radius-small);
  ${(props) =>
    props.disabled ? alpha.primaryDisabled.body3 : alpha.primaryInactive.body3};

  :focus {
    outline: none;
    ${alpha.primaryActive.body3}
  }

  ::placeholder {
    ${alpha.primaryInactive.body3};
  }

  ::selection {
    ${alpha.primaryActive.body3}
    background-color: var(--color-secondary-200);
  }
`;

const StyledIconContainer = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
  ${(props) =>
    props.hasError &&
    css`
      svg {
        --main-color: var(--color-error-500);
      }
    `}
`;

type TextAreaProps = BaseComponentProps<"textarea"> & {
  caption?: string;
  errorMessage?: string;
  iconName?: IconName;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;

  /** Function called when the input looses focus */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;

  /** Function called when the input value changes */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

const TextAreaComponent = ({
  caption,
  errorMessage,
  iconName,
  onBlur,
  onChange,
  ...inputProps
}: TextAreaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [focused, textAreaRef]);

  return (
    <>
      <StyledTextAreaContainer
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        hasError={!!errorMessage}
      >
        <StyledTextArea
          ref={textAreaRef}
          onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
            onBlur && onBlur(event);
            setFocused(false);
          }}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange && onChange(event);
          }}
          onFocus={() => setFocused(true)}
          placeholder={inputProps.placeholder}
          {...inputProps}
        />

        {iconName && (
          <StyledIconContainer hasError={!!errorMessage}>
            <SmallPlainIcon
              iconName={errorMessage ? "exclamation" : iconName}
              variant={
                inputProps.disabled || inputProps.readOnly
                  ? "DISABLED"
                  : focused
                  ? "ACTIVE"
                  : "DEFAULT"
              }
            />
          </StyledIconContainer>
        )}
      </StyledTextAreaContainer>
      {caption && (
        <Caption
          isDisabled={inputProps.disabled || inputProps.readOnly}
          hasError={!!errorMessage}
        >
          {errorMessage || caption}
        </Caption>
      )}
    </>
  );
};

export const TextArea = (props: TextAreaProps) => {
  const { label, name, disabled, errorMessage, isRequired } = props;

  if (label) {
    return (
      <label htmlFor={name}>
        <LabelText isDisabled={disabled} hasError={!!errorMessage}>
          {label} {isRequired ? <Asterisk>*</Asterisk> : null}
        </LabelText>
        <TextAreaComponent {...props} />
      </label>
    );
  }
  return <TextAreaComponent {...props} />;
};
